/**
 * 授信环节 - 权益卡片（Composition API）程序化入口
 * 概要接口请求在此执行，组件仅负责视图渲染
 * 使用方请从 '@/widgets/right-buy-card/super-member-card/entry' 导入
 */
import Vue from 'vue';
import router from '@/router';
import store from '@/store';
import { getCreditBuyEquity, getSuperMemberCardSummary } from '@/api/rights';
import { preCreateRightOrder } from '@/api/rights/order/order';
import { ContractReadStatusEnum } from '@/widgets/right-contract/enum';
import {
  PackageBuyStatusEnum,
  SuperMemberSourceEnum,
  SuperMemberSceneEnum,
} from '@/api/rights/types';
import type { BuyEquityResponseType, SuperCardSummaryBackType } from '@/api/rights/types';
import type { ForceReadServiceReturnType } from '@/widgets/right-contract';
import type { ApplyChooseList } from '@/views_v2/pre-apply-choose/types';
import {
  reportForceReadError,
  reportMemberGetshowError,
  reportModuleExpose,
  buildTqParams,
} from './tianqi';
import '@/views_v2/pre-apply-choose/store';

import SuperMemberCardComponent from './index.vue';

/** getCreditBuyEquity 所需商品列表，由调用方在 init 时传入 */
export type SuperMemberCardProductItem = { productId: number; followLoan: boolean };

/**
 * init 入参：productIds 与 sceneType 二选一必填
 * - 传 productIds：走授信概要 getCreditBuyEquity，需同时传 productList
 * - 传 sceneType：走场景概要 getSuperMemberCardSummary
 */
export interface SuperMemberCardInitParams {
  /** 授信协议场景：商品 ID 列表，与 productList 一起用于 getCreditBuyEquity */
  productIds?: number[];
  /** 授信协议场景：商品列表，与 productIds 一起用于 getCreditBuyEquity，由调用方传入 */
  productList?: SuperMemberCardProductItem[];
  /** 非授信协议场景：场景类型，用于 getSuperMemberCardSummary */
  sceneType?: SuperMemberSceneEnum;
  /** 来源 */
  from?: SuperMemberSourceEnum;
}

/** 内部 wrapper 状态，含 init 入参与 fetchSummary 写入的 rightInfo/buyStatus */
interface WrapperAppData extends SuperMemberCardInitParams {
  /** 概要接口返回，由 fetchSummary 写入 */
  rightInfo: BuyEquityResponseType | SuperCardSummaryBackType | null;
  /** 购买状态，由 fetchSummary 写入 */
  buyStatus: PackageBuyStatusEnum | null;
}

type SuperMemberCardInstance = Vue & {
  buyStatus: PackageBuyStatusEnum | null;
  rightInfo: BuyEquityResponseType | SuperCardSummaryBackType | null;
  checked: boolean;
  preChooseContract: ForceReadServiceReturnType | null;
  applyChooseList: ApplyChooseList | null;
  from: SuperMemberSourceEnum;
};

let wrapperApp: (Vue & WrapperAppData) | null = null;

function destroyInstance(): void {
  if (wrapperApp) {
    wrapperApp.$destroy();
    wrapperApp = null;
  }
}

/** 路由变化时销毁单例，避免热更新或重复进入导致状态错乱 */
router.afterEach(() => {
  destroyInstance();
});

function getRootInstance(): SuperMemberCardInstance | null {
  if (!wrapperApp || !wrapperApp.$children.length) {
    return null;
  }
  return wrapperApp.$children[0] as SuperMemberCardInstance;
}

/**
 * 请求超级会员概要接口，结果写入 wrapper 的 rightInfo / buyStatus
 * 使用 init 传入的 productIds+productList 或 sceneType，不从 store 取数
 */
async function fetchSummary(app: Vue & WrapperAppData): Promise<void> {
  const productList = app.productList || [];

  try {
    if (app.productIds != null
      && app.productIds.length > 0
      && app.from === SuperMemberSourceEnum.CREDIT_AGREEMENT) {
      app.rightInfo = await getCreditBuyEquity({
        productIds: app.productIds,
        productList,
      });
    } else if (app.sceneType != null) {
      app.rightInfo = await getSuperMemberCardSummary({
        sceneType: app.sceneType,
        // productList,
      } as Parameters<typeof getSuperMemberCardSummary>[0]);
    } else {
      return;
    }

    const info = app.rightInfo;
    app.buyStatus = info?.packageBuyStatus || null;
    const commoditys = app.productIds || [];
    if (app.buyStatus) {
      const instance = getRootInstance();
      if (instance) {
        reportModuleExpose(instance, buildTqParams(
          app.rightInfo as Record<string, unknown> | null,
          app.from || SuperMemberSourceEnum.CREDIT_AGREEMENT,
          commoditys,
        ));
      }
    }
  } catch (err) {
    reportMemberGetshowError(null, err);
  }
}

function getInstance(
  element?: HTMLElement,
  params?: SuperMemberCardInitParams,
): SuperMemberCardInstance | null {
  // 若已有实例但已不在当前文档（如从其他页切到 unit-agreement），则销毁以便挂到新节点
  if (wrapperApp && element && !document.contains(wrapperApp.$el)) {
    destroyInstance();
  }

  if (!wrapperApp && element) {
    const app = new Vue({
      router,
      store,
      data(): WrapperAppData {
        return {
          productIds: params?.productIds,
          productList: params?.productList,
          sceneType: params?.sceneType,
          from: params?.from || SuperMemberSourceEnum.CREDIT_AGREEMENT,
          rightInfo: null,
          buyStatus: null,
        };
      },
      render(createElement) {
        const self = this as Vue & WrapperAppData;
        return createElement(SuperMemberCardComponent, {
          props: {
            commoditys: self.productIds || [],
            from: self.from || SuperMemberSourceEnum.CREDIT_AGREEMENT,
            sceneType: self.sceneType || SuperMemberSceneEnum.FQ_MALL_AGREEMENT_SCENE,
            rightInfo: self.rightInfo,
            buyStatus: self.buyStatus,
            onRefresh: () => { fetchSummary(self); },
          },
        });
      },
    }) as Vue & WrapperAppData;
    wrapperApp = app;
    app.$mount(element);
    return getRootInstance();
  }

  if (wrapperApp && params) {
    wrapperApp.productIds = params.productIds;
    wrapperApp.productList = params.productList;
    wrapperApp.sceneType = params.sceneType;
    wrapperApp.from = params.from || SuperMemberSourceEnum.CREDIT_AGREEMENT;
    return getRootInstance();
  }

  return getRootInstance();
}

async function init(
  params: SuperMemberCardInitParams,
  element: HTMLElement,
): Promise<void> {
  const hasProductIds = params.productIds != null && params.productIds.length > 0;
  const hasSceneType = params.sceneType != null;
  if (!hasProductIds && !hasSceneType) {
    return;
  }
  const instance = getInstance(element, params);
  if (!wrapperApp) return;
  await fetchSummary(wrapperApp);
}

function needBuySuperMemberCard(): boolean {
  const instance = getRootInstance();
  return !!instance?.checked && instance?.buyStatus === PackageBuyStatusEnum.NOT_BUY;
}

function getSuperMember(): {
  isSuperMemberCardShow: boolean;
  buyStatus?: PackageBuyStatusEnum | null;
  hasBuy?: boolean;
  } {
  const instance = getRootInstance();
  if (instance) {
    return {
      isSuperMemberCardShow: !!instance.buyStatus,
      buyStatus: instance.buyStatus,
      hasBuy: instance.buyStatus === PackageBuyStatusEnum.BOUGHT,
    };
  }
  return { isSuperMemberCardShow: false };
}

async function buyRights(taskId: string): Promise<{ orderId: string }> {
  const instance = getRootInstance();
  const { packageId = '' } = instance?.rightInfo || {};
  const followLoan = !!(
    instance?.applyChooseList?.commoditys?.some((item: { followLoan?: boolean }) => item.followLoan)
  );

  if (
    instance?.preChooseContract?.readStatus === ContractReadStatusEnum.NEED_READ_NOT_READ
  ) {
    reportForceReadError();
  }

  const res = await preCreateRightOrder({
    packageId,
    creditTaskId: taskId,
    from: instance?.from || SuperMemberSourceEnum.CREDIT_AGREEMENT,
    followLoan,
  });
  return res;
}

/** 为组件挂载静态方法并导出；.vue 导入无泛型，经 unknown 断言后挂载静态方法 */
type SuperMemberCardExport = typeof SuperMemberCardComponent & {
  init: typeof init;
  destroyInstance: typeof destroyInstance;
  needBuySuperMemberCard: typeof needBuySuperMemberCard;
  getSuperMember: typeof getSuperMember;
  buyRights: typeof buyRights;
};
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
const SuperMemberCardComposition = SuperMemberCardComponent as unknown as SuperMemberCardExport;

SuperMemberCardComposition.init = init;
SuperMemberCardComposition.destroyInstance = destroyInstance;
SuperMemberCardComposition.needBuySuperMemberCard = needBuySuperMemberCard;
SuperMemberCardComposition.getSuperMember = getSuperMember;
SuperMemberCardComposition.buyRights = buyRights;

export default SuperMemberCardComposition;
